import { useState, useEffect } from 'react';
import { merchantAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Bell, UserCheck, BellOff, Headphones } from 'lucide-react';
import toast from 'react-hot-toast';

const HANDOFF_DEFAULTS = {
  enabled: true,
  handoffMessage: "I've let our team know. Someone will reply right here as soon as they can.",
  resumeAfterMinutes: 10,
  resumeMessage: "Sorry, our team hasn't been able to reply yet. I'll keep helping you in the meantime, and they'll still see this chat.",
};

const PUSH_MODES = [
  { value: 'all', icon: Bell, title: 'All messages', desc: 'Get a push for every new customer message.' },
  { value: 'agent_only', icon: UserCheck, title: 'Only when someone asks for a person', desc: 'Notify only when a customer asks to talk to a real person instead of the AI.' },
  { value: 'off', icon: BellOff, title: 'Off', desc: 'No push notifications.' },
];

export default function ProfilePage() {
  const { merchant, user, updateMerchant } = useAuth();
  const [form, setForm] = useState({ businessName: '', phone: '', website: '', timezone: 'UTC', currency: 'USD', settings: { welcomeMessage: '', offHoursMessage: '' } });
  const [pushMode, setPushMode] = useState('all');
  const [handoff, setHandoff] = useState(HANDOFF_DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (merchant) {
      setForm({ businessName: merchant.businessName || '', phone: merchant.phone || '', website: merchant.website || '', timezone: merchant.timezone || 'UTC', currency: merchant.currency || 'USD', settings: { welcomeMessage: merchant.settings?.welcomeMessage || '', offHoursMessage: merchant.settings?.offHoursMessage || '' } });
      setPushMode(merchant.notificationSettings?.pushMode || 'all');
      setHandoff({ ...HANDOFF_DEFAULTS, ...(merchant.handoffConfig || {}) });
    }
  }, [merchant]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const [{ data }, , { data: handoffRes }] = await Promise.all([
        merchantAPI.update(form),
        merchantAPI.updateNotifications({ pushMode }),
        merchantAPI.updateHandoffConfig({ ...handoff, resumeAfterMinutes: Number(handoff.resumeAfterMinutes) || 0 }),
      ]);
      updateMerchant({ ...data.data, notificationSettings: { ...data.data.notificationSettings, pushMode }, handoffConfig: handoffRes.data });
      toast.success('Settings saved');
    } catch (_) {}
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const setNested = (parent, k) => (e) => setForm(p => ({ ...p, [parent]: { ...p[parent], [k]: e.target.value } }));

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Settings</h1><p className="page-subtitle">Manage your workspace profile and preferences</p></div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}><Save size={15} />{saving ? 'Saving…' : 'Save'}</button>
      </div>
      <div className="page-body">
        <div className="grid-2" style={{ gap: 16, alignItems: 'start' }}>
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 14 }}>Business Profile</div>
              <div className="form-group"><label className="form-label">Business Name</label><input className="form-input" value={form.businessName} onChange={set('businessName')} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={set('phone')} /></div>
              <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.website} onChange={set('website')} /></div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Timezone</label>
                  <select className="form-input" value={form.timezone} onChange={set('timezone')}>
                    {['UTC','America/New_York','America/Los_Angeles','Europe/London','Asia/Kuala_Lumpur','Asia/Singapore','Asia/Tokyo'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Currency</label>
                  <select className="form-input" value={form.currency} onChange={set('currency')}>
                    {['USD','EUR','GBP','MYR','SGD','JPY','AUD'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-title" style={{ marginBottom: 4 }}>Push Notifications</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 14 }}>When your team's phones and browsers get alerted about new chats. Applies to everyone in this workspace.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {PUSH_MODES.map(({ value, icon: Icon, title, desc }) => {
                  const active = pushMode === value;
                  return (
                    <label key={value} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 10, cursor: 'pointer', background: active ? 'var(--primary-light, rgba(99,102,241,0.06))' : 'transparent' }}>
                      <input type="radio" name="pushMode" value={value} checked={active} onChange={() => setPushMode(value)} style={{ marginTop: 3 }} />
                      <Icon size={18} style={{ marginTop: 1, color: active ? 'var(--primary)' : 'var(--text-muted)', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{title}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Headphones size={17} /> Live Agent Handoff</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={handoff.enabled} onChange={e => setHandoff(p => ({ ...p, enabled: e.target.checked }))} />
                  {handoff.enabled ? 'On' : 'Off'}
                </label>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 14 }}>
                When a customer asks for a person, the AI hands the chat to your team. When a customer seems frustrated, it alerts your team and offers a person. Alerts go to the inbox, browsers and the phone app. Channels with "live agent" turned off are skipped.
              </div>
              {handoff.enabled && (
                <>
                  <div className="form-group">
                    <label className="form-label">Message when handing over</label>
                    <textarea className="form-input" rows={2} value={handoff.handoffMessage} onChange={e => setHandoff(p => ({ ...p, handoffMessage: e.target.value }))} />
                    <div className="form-hint">Avoid promising a call or a time. Your team may not be free straight away.</div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">AI takes over again if nobody replies within (minutes)</label>
                    <input className="form-input" type="number" min={0} max={1440} value={handoff.resumeAfterMinutes} onChange={e => setHandoff(p => ({ ...p, resumeAfterMinutes: e.target.value }))} style={{ maxWidth: 120 }} />
                    <div className="form-hint">0 = never. The chat waits for your team.</div>
                  </div>
                  {Number(handoff.resumeAfterMinutes) > 0 && (
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Message when the AI takes over again</label>
                      <textarea className="form-input" rows={2} value={handoff.resumeMessage} onChange={e => setHandoff(p => ({ ...p, resumeMessage: e.target.value }))} />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="card">
              <div className="card-title" style={{ marginBottom: 14 }}>Automated Messages</div>
              <div className="form-group"><label className="form-label">Welcome Message</label><textarea className="form-input" rows={3} value={form.settings.welcomeMessage} onChange={setNested('settings', 'welcomeMessage')} placeholder="Hello! How can I help you today?" /></div>
              <div className="form-group"><label className="form-label">Off-Hours Message</label><textarea className="form-input" rows={3} value={form.settings.offHoursMessage} onChange={setNested('settings', 'offHoursMessage')} placeholder="We're currently closed. We'll reply during business hours." /></div>
              <div className="card" style={{ background: 'var(--bg)', marginBottom: 0 }}>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}><strong>Logged in as:</strong> {user?.name} ({user?.email})</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>Role: {user?.role}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
